import { NSARouterMakerResult } from "namirasoft-account-react";
import { SortItem } from "namirasoft-core";
import { RouterRouteCcgType } from "namirasoft-inference";
import { NSBaseMetaTable } from "namirasoft-site";
import { NamirasoftMap } from "namirasoft-site-map";
import { IBaseComponentProps, NSBox, NSBoxEntity, NSBoxEnum, NSInfoProps } from "namirasoft-site-react";
import { Component, createRef, Fragment } from "react";
import { InferenceRouterProps } from "../InferenceRouterProps";

interface CCGEntityRow
{
    id: string;
    name: string;
}

export interface CCGTypeIDValue
{
    ccg_type: RouterRouteCcgType;
    ccg_id: string;
}

export interface CCGTypeIDProps extends IBaseComponentProps, NSARouterMakerResult, InferenceRouterProps
{
    typeTitle: string;
    idTitle: string;
    required?: boolean;
    reloadable?: boolean;
    width?: string;
    onChanged?: () => void;
    info?: NSInfoProps;
}

interface CCGTypeIDState
{
    ccg_type: RouterRouteCcgType;
}

export default class CCGTypeID extends Component<CCGTypeIDProps, CCGTypeIDState>
{
    private NSBoxEnum_Type = createRef<NSBoxEnum<RouterRouteCcgType>>();
    private NSBoxEntity_Id = createRef<NSBoxEntity<CCGEntityRow>>();

    constructor(props: CCGTypeIDProps)
    {
        super(props);
        this.state = { ccg_type: RouterRouteCcgType.Condition };
        this.getValue = this.getValue.bind(this);
        this.setValue = this.setValue.bind(this);
        this.onTypeChanged = this.onTypeChanged.bind(this);
        this.onIdChanged = this.onIdChanged.bind(this);
    }

    getValue(checkError: boolean = false): CCGTypeIDValue
    {
        const ccg_type = this.NSBoxEnum_Type.current?.getValueEnumOne(RouterRouteCcgType, RouterRouteCcgType.Condition, checkError) ?? RouterRouteCcgType.Condition;
        const ccg_id = this.NSBoxEntity_Id.current?.getValueOne(checkError) ?? "";
        return { ccg_type, ccg_id };
    }

    setValue(value: CCGTypeIDValue): void
    {
        this.setState({ ccg_type: value.ccg_type }, () =>
        {
            this.NSBoxEnum_Type.current?.setValue(value.ccg_type);
            this.NSBoxEntity_Id.current?.setValue(value.ccg_id);
        });
    }

    private resolveTable(namirasoft: NamirasoftMap): NSBaseMetaTable<any, any>
    {
        switch (this.state.ccg_type)
        {
            case RouterRouteCcgType.Condition: return namirasoft.inference.tables.condition;
            case RouterRouteCcgType.ConditionGroup: return namirasoft.inference.tables.condition_group;
            default: return namirasoft.inference.tables.condition;
        }
    }

    private onTypeChanged(): void
    {
        const next = this.NSBoxEnum_Type.current?.getValueEnumOne(RouterRouteCcgType, RouterRouteCcgType.Condition, false) ?? RouterRouteCcgType.Condition;
        if (next !== this.state.ccg_type)
            this.setState({ ccg_type: next }, () =>
            {
                this.NSBoxEntity_Id.current?.setValue(null);
                this.props.onChanged?.();
            });
        else
            this.props.onChanged?.();
    }

    private onIdChanged(): void
    {
        this.props.onChanged?.();
    }

    override render()
    {
        const namirasoft = new NamirasoftMap(this.props.account.token_manager, console.error);
        const table = this.resolveTable(namirasoft);
        const required = this.props.required ?? true;
        const width = this.props.width ?? NSBox.width.one;
        return (
            <Fragment>
                <NSBoxEnum<RouterRouteCcgType>
                    ref={this.NSBoxEnum_Type}
                    title={this.props.typeTitle}
                    required={required}
                    multiple={false}
                    reloadable={this.props.reloadable ?? false}
                    getEnumObject={() => RouterRouteCcgType}
                    onChanged={this.onTypeChanged}
                    style={{ width, minWidth: width, flexShrink: 0 }}
                    info={this.props.info}
                />
                <NSBoxEntity<CCGEntityRow>
                    ref={this.NSBoxEntity_Id}
                    key={`ccg-id-${this.state.ccg_type}`}
                    title={this.props.idTitle}
                    required={required}
                    multiple={false}
                    table={table}
                    getValue={(item) => item.id}
                    getTitle={(item) => item.name}
                    getSort={() => new SortItem(table, (table as any).columns.name, true)}
                    onChanged={this.onIdChanged}
                    style={{ width, minWidth: width, flexShrink: 0 }}
                    info={this.props.info}
                />
            </Fragment>
        );
    }
}